import React, { useEffect, useRef, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportEnginesSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { BoilersUsageTypeEnum, BoilerTypeEnumV3, EngineDataTypeEnum, NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import {
    createSectionData,
    getBoolValue,
    getConsumptionCost,
    getListValue,
    getNumberValue,
    getStringValue,
    initializeConsumption,
    intializeBoilersConsumptionByUsage,
} from '../../../utils/aolFieldsHelper';
import { SectionInnerWrapper } from '../aol-container/AolContainer.style';
import AuxiliaryEnginesSectionComponent from './auxiliary-engines/AuxiliaryEnginesSection';
import BoilersSectionComponent from './boilers/BoilersSection';
import PumpsAndFansSectionComponent from './pumps-and-fans/PumpsAndFansSection';
import EconomizerSectionComponent from './economizer/EconomizerSection';
import { EnginesSectionWrapper, ColumnsWrapper, Column } from './EnginesSection.style';
import MainEngineSectionComponent from './main-engine/MainEngineSection';
import IncineratorSectionComponent from './incinerator/IncineratorSection';
import { roundTo2Decimals } from '../../../utils/numberHelper';
import useAolItemsHelper from '../../../utils/aol/useAolItemsHelper';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import { isDateBefore, isDateSame, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';
import SpinnerLoaderComponent from '../../common/loader/SpinnerLoader';
import ShorePowerSectionComponent from './shore-power/ShorePowerSection';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { BoilerConsumptionV5 } from 'src/types/ViewModel/Aol/BoilerConsumptionV5';
import { useBunkerContext } from 'src/context/BunkerContext';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';

type Props = {
    numberOfColumns: number;
};

export type EngineDataVM = {
    runningHours?: number;
    powerLoggedOutput?: number;
    powerOutput?: number;
    pitch?: number;
    revolutionsPerMinute?: number;
    consumptionHfo?: number;
    consumptionMgo?: number;
    engineLoad?: number;
    consumptionHfoCost?: number;
    consumptionMgoCost?: number;
    type?: number;
    engineNumber?: number;
};

export type EnginesDataVM = {
    mainEngineData: EngineDataVM;
    economizerRunningHours?: number;
    economizerDateOfLastCleaning?: Date;
    isEconomizerSootBlowingEquipmentInOperationalCondition?: boolean;
    auxiliaryEnginesConsumptionHfo?: number;
    auxiliaryEnginesConsumptionMgo?: number;
    boilersConsumptionHfo?: number;
    boilersConsumptionHfoCost?: number;
    boilersConsumptionMgo?: number;
    boilersConsumptionMgoCost?: number;
    boilersConsumptionForCargoHeating?: number;
    boilersConsumptionForCargoHeatingMgo?: number;
    boilersConsumptionForEngineRoomAndAccommodation?: number;
    boilersConsumptionForEngineRoomAndAccommodationMgo?: number;
    boilersConsumptionForFreshWaterProduction?: number;
    boilersConsumptionForFreshWaterProductionMgo?: number;
    boilersConsumptionForTankCleaning?: number;
    boilersConsumptionForTankCleaningMgo?: number;
    boilersConsumptionForOther?: number;
    boilersConsumptionForOtherMgo?: number;
    boilersConsumptionForOtherText?: string;
    arePumpsAndFansSwitchedOff?: boolean;
    pumpsAndFansNotSwitchedOffReason?: string;
    incineratorConsumptionMgo?: number;
    isSurplusSteamAvailable?: boolean;
    isSurplusSteamUsedForFreshWaterProduction?: boolean;
    incineratorConsumptionMgoCost?: number;
    auxiliaryEnginesConsumptionHfoCost?: number;
    auxiliaryEnginesConsumptionMgoCost?: number;
    boilersData?: EngineDataVM[];
    auxiliaryEnginesData?: EngineDataVM[];
    shaftGeneratorData?: EngineDataVM;
    shorePowerRunningHours?: number;
    isShorePowerConnected?: boolean;
    shorePowerKwhReceived?: number;
    observedSpeed?: number;
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
    slipDetails?: string[];
    sfocDetails?: string[];
    consumption?: Consumption[] | null;
    boilerConsumptionV5?: BoilerConsumptionV5[] | null;
};

const EnginesSectionComponent: React.FC<Props> = ({ numberOfColumns }) => {
    const { vessel } = useVesselStore();
    const [enginesDataViewModel, setEnginesDataViewModel] = useState<EnginesDataVM>({ mainEngineData: {} });
    const [hasEconomizer, setHasEconomizer] = useState(false);
    const { selectedAolDataItem, aolGroups, setSelectedAolDataItem, aolSyncEvent } = useAolStore();
    const { fuelRobData, bunkerings } = useBunkerContext();
    const hasEmptyMainValues = useRef(false);
    const hasEmptyAuxValues = useRef(false);
    const hasEmptyBoilerValues = useRef(false);
    const hasEmptyPumpAndFansValues = useRef(false);
    const hasEmptyIncineratorValues = useRef(false);
    const hasEmptyEconomizerValues = useRef(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const { getVoyageFuelPrice } = useAolItemsHelper();
    const { isLoadingEndpoint } = useApiLoaderHelper();
    const { checkIsAolReadOnly } = useReadOnlyUtils();
    const { isFeatureEnabled } = useAppFeature();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (
                saveAolEvent.type === 'save_section' &&
                !hasEmptyMainValues.current &&
                !hasEmptyAuxValues.current &&
                !hasEmptyBoilerValues.current &&
                !hasEmptyPumpAndFansValues.current &&
                !hasEmptyIncineratorValues.current &&
                !hasEmptyEconomizerValues.current
            )
                save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!selectedAolDataItem || !vessel?.vesselConfigurationV3) return;
        if (
            selectedAolDataItem?.aolVersion >= 5 &&
            isFeatureEnabled({ featureEnum: AppFeatureEnum.Bunkering, imoNumber: vessel.imoNumber, date: selectedAolDataItem.reportDate })
        ) {
            if (!fuelRobData || !bunkerings) return;
        }

        if (!enginesDataViewModel.isInitialized) {
            initVM();
        }
        setHasEconomizer(vessel.vesselConfigurationV3.engineConfiguration.boilers.some((b) => b.boilerType === BoilerTypeEnumV3.Economizer));
    }, [selectedAolDataItem, fuelRobData, bunkerings, vessel]);

    useEffect(() => {
        if (!enginesDataViewModel) return;
        updateTotalConsumption();
    }, [enginesDataViewModel]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(enginesDataViewModel?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;

        const noonReport = selectedAolDataItem.noonReport;
        const voyageFuelPrice = getVoyageFuelPrice(selectedAolDataItem);

        const mainEngineVM: EngineDataVM = {
            runningHours: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.runningHours, 0, true),
            powerLoggedOutput: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.powerLoggedOutput, 2, true),
            powerOutput: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.powerOutput, 2, true),
            revolutionsPerMinute: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.revolutionsPerMinute, 1, true),
            consumptionHfo: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.consumptionHfo, 2, true),
            consumptionMgo: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.consumptionMgo, 2, true),
            pitch: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.pitch, 1, true),
            consumptionHfoCost: getConsumptionCost(FuelType.HFO, voyageFuelPrice, noonReport?.enginesSection?.mainEnginesData?.consumptionHfo),
            consumptionMgoCost: getConsumptionCost(FuelType.LGO, voyageFuelPrice, noonReport?.enginesSection?.mainEnginesData?.consumptionMgo),
            type: noonReport?.enginesSection?.mainEnginesData?.type ?? EngineDataTypeEnum.MainEngine,
        };

        let auxiliaryEngineVMs: EngineDataVM[] = [];
        const auxiliaryEnginesListConfig = vessel?.vesselConfigurationV3?.engineConfiguration.auxiliaryEnginesList;

        const numberOfAuxEngines = auxiliaryEnginesListConfig ? auxiliaryEnginesListConfig.length : 4;

        for (let i = 0; i < numberOfAuxEngines; i++) {
            const aux = noonReport?.enginesSection?.auxiliaryEnginesData ? noonReport?.enginesSection.auxiliaryEnginesData[i] : null;
            const vm: EngineDataVM = {
                runningHours: getNumberValue(aux?.runningHours, 0, true),
                powerLoggedOutput: getNumberValue(aux?.powerLoggedOutput, 2, true),
                powerOutput: getNumberValue(aux?.powerOutput, 2, true),
                consumptionHfo: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.consumptionHfo, 2, true),
                consumptionMgo: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.consumptionMgo, 2, true),
                pitch: getNumberValue(noonReport?.enginesSection?.mainEnginesData?.pitch, 1, true),
                type: aux?.type ?? EngineDataTypeEnum.AuxiliaryEngine,
                engineNumber: aux?.engineNumber ?? i + 1,
            };

            auxiliaryEngineVMs.push(vm);
        }

        auxiliaryEngineVMs = auxiliaryEngineVMs.sort((a, b) => a.engineNumber - b.engineNumber);

        const shaftGeneratorDataVMs: EngineDataVM = {
            runningHours: getNumberValue(noonReport?.enginesSection?.shaftGeneratorData?.runningHours, 0, true),
            powerLoggedOutput: getNumberValue(noonReport?.enginesSection?.shaftGeneratorData?.powerLoggedOutput, 2, true),
            powerOutput: getNumberValue(noonReport?.enginesSection?.shaftGeneratorData?.powerOutput, 2, true),
            type: noonReport?.enginesSection?.shaftGeneratorData?.type ?? EngineDataTypeEnum.ShaftGenerator,
        };

        const boilersData: EngineDataVM[] = [];
        let boilerDataList = getListValue(noonReport?.enginesSection?.boilersData);
        boilerDataList = boilerDataList.sort((a, b) => a.engineNumber - b.engineNumber);
        for (let i = 0; i < 3; i++) {
            const boilerData: EngineDataVM = {
                runningHours: getNumberValue(boilerDataList[i]?.runningHours, 0, true),
                type: boilerDataList[i]?.type ?? EngineDataTypeEnum.Boilers,
            };

            boilersData.push(boilerData);
        }

        const _consumption =
            selectedAolDataItem.aolVersion >= 5
                ? initializeConsumption(
                      noonReport?.enginesSection?.consumption,
                      voyageFuelPrice,
                      aolGroups,
                      selectedAolDataItem,
                      fuelRobData,
                      bunkerings
                  )
                : [];

        const _boilersConsumptionV5 =
            selectedAolDataItem.aolVersion >= 5
                ? intializeBoilersConsumptionByUsage(
                      _consumption ?? [],
                      noonReport?.enginesSection?.boilerConsumptionV5 ?? [],
                      noonReport?.metadata?.noonReportId
                  )
                : [];

        const enginesData: EnginesDataVM = {
            mainEngineData: mainEngineVM,
            auxiliaryEnginesData: auxiliaryEngineVMs,
            shaftGeneratorData: shaftGeneratorDataVMs,
            auxiliaryEnginesConsumptionHfo: getNumberValue(noonReport?.enginesSection?.auxiliaryEnginesConsumptionHfo, 2, true),
            auxiliaryEnginesConsumptionMgo: getNumberValue(noonReport?.enginesSection?.auxiliaryEnginesConsumptionMgo, 2, true),
            economizerRunningHours: getNumberValue(noonReport?.enginesSection?.economizerRunningHours, 0, true),
            economizerDateOfLastCleaning: getEconomizerDateOfLastCleaning(),
            isEconomizerSootBlowingEquipmentInOperationalCondition:
                getBoolValue(noonReport?.enginesSection?.isEconomizerSootBlowingEquipmentInOperations) ?? false,
            boilersConsumptionHfo: getNumberValue(noonReport?.enginesSection?.boilersConsumptionHfo, 2, true),
            boilersConsumptionMgo: getNumberValue(noonReport?.enginesSection?.boilersConsumptionMgo, 2, true),
            boilersData: boilersData,
            boilersConsumptionForCargoHeating: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForCargoHeating, 2, true),
            boilersConsumptionForCargoHeatingMgo: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForCargoHeatingMgo, 2, true),
            boilersConsumptionForEngineRoomAndAccommodation: getNumberValue(
                noonReport?.enginesSection?.boilersConsumptionForEngineRoomAndAccommodation,
                2,
                true
            ),
            boilersConsumptionForEngineRoomAndAccommodationMgo: getNumberValue(
                noonReport?.enginesSection?.boilersConsumptionForEngineRoomAndAccommodationMgo,
                2,
                true
            ),
            boilersConsumptionForFreshWaterProduction: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForFreshWaterProduction, 2, true),
            boilersConsumptionForFreshWaterProductionMgo: getNumberValue(
                noonReport?.enginesSection?.boilersConsumptionForFreshWaterProductionMgo,
                2,
                true
            ),
            boilersConsumptionForTankCleaning: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForTankCleaning, 2, true),
            boilersConsumptionForTankCleaningMgo: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForTankCleaningMgo, 2, true),
            boilersConsumptionForOther: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForOther, 2, true),
            boilersConsumptionForOtherMgo: getNumberValue(noonReport?.enginesSection?.boilersConsumptionForOtherMgo, 2, true),
            boilersConsumptionForOtherText: getStringValue(noonReport?.enginesSection?.boilersConsumptionForOtherText),
            arePumpsAndFansSwitchedOff: getBoolValue(noonReport?.enginesSection?.arePumpsAndFansSwitchedOff) ?? false,
            pumpsAndFansNotSwitchedOffReason: getStringValue(noonReport?.enginesSection?.pumpsAndFansNotSwitchedOffReason),
            incineratorConsumptionMgo: getNumberValue(noonReport?.enginesSection?.incineratorConsumptionMgo, 2, true),
            isSurplusSteamAvailable: getBoolValue(noonReport?.enginesSection?.isSurplusSteamAvailable) ?? false,
            isSurplusSteamUsedForFreshWaterProduction: getBoolValue(noonReport?.enginesSection?.isSurplusSteamUsedForFreshWaterProduction) ?? false,
            shorePowerRunningHours: getNumberValue(noonReport?.enginesSection?.shorePowerRunningHours, 2, false),
            isShorePowerConnected: noonReport?.enginesSection?.isShorePowerConnected ?? getIsShorePowerConnectedFromPrevious(),
            shorePowerKwhReceived: getNumberValue(noonReport?.enginesSection?.shorePowerKwhReceived, 2, false),
            auxiliaryEnginesConsumptionHfoCost: getConsumptionCost(
                FuelType.HFO,
                voyageFuelPrice,
                noonReport?.enginesSection?.auxiliaryEnginesConsumptionHfo
            ),
            auxiliaryEnginesConsumptionMgoCost: getConsumptionCost(
                FuelType.LGO,
                voyageFuelPrice,
                noonReport?.enginesSection?.auxiliaryEnginesConsumptionMgo
            ),
            boilersConsumptionHfoCost: getConsumptionCost(FuelType.HFO, voyageFuelPrice, noonReport?.enginesSection?.boilersConsumptionHfo),
            boilersConsumptionMgoCost: getConsumptionCost(FuelType.LGO, voyageFuelPrice, noonReport?.enginesSection?.boilersConsumptionMgo),
            incineratorConsumptionMgoCost: getConsumptionCost(FuelType.LGO, voyageFuelPrice, noonReport?.enginesSection?.incineratorConsumptionMgo),
            observedSpeed: getNumberValue(noonReport?.bridgeSection?.observedSpeed, 2, true),
            sectionData: noonReport?.enginesSection?.sectionData ?? createSectionData(NoonReportSectionEnum.Engines),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
            consumption: _consumption,
            boilerConsumptionV5: _boilersConsumptionV5,
        };

        selectedAolDataItem.aolDataItemProperties.isSurplusSteamUsedForFreshWaterProduction = enginesData.isSurplusSteamUsedForFreshWaterProduction;

        setEnginesDataViewModel({ ...enginesData });
    };

    const updateTotalConsumption = () => {
        if (!selectedAolDataItem) return;
        const properties = selectedAolDataItem.aolDataItemProperties;
        properties.totalHfoConsumption =
            getNumberValue(enginesDataViewModel.auxiliaryEnginesConsumptionHfo, undefined, true) +
            getNumberValue(enginesDataViewModel.boilersConsumptionHfo, undefined, true) +
            getNumberValue(enginesDataViewModel.mainEngineData.consumptionHfo, undefined, true);
        properties.totalMgoConsumption =
            getNumberValue(enginesDataViewModel.auxiliaryEnginesConsumptionMgo, undefined, true) +
            getNumberValue(enginesDataViewModel.boilersConsumptionMgo, undefined, true) +
            getNumberValue(enginesDataViewModel.mainEngineData.consumptionMgo, undefined, true);
        setSelectedAolDataItem({ ...selectedAolDataItem });
    };

    const getEconomizerDateOfLastCleaning = () => {
        if (selectedAolDataItem?.noonReport?.enginesSection?.economizerDateOfLastCleaning) {
            return selectedAolDataItem?.noonReport?.enginesSection?.economizerDateOfLastCleaning;
        }
        const aolDataItems = aolGroups.flatMap((a) => a.aolDataItems);
        const sortedAolDataItems = aolDataItems
            .filter((x) => isDateBefore(x.reportDate, selectedAolDataItem.reportDate))
            .sort((a, b) => (isDateBefore(a.reportDate, b.reportDate) ? 1 : -1));

        const lastCleaningAol = sortedAolDataItems.find((x) => x.noonReport?.enginesSection?.economizerDateOfLastCleaning);
        if (lastCleaningAol?.noonReport?.enginesSection?.economizerDateOfLastCleaning)
            return lastCleaningAol.noonReport.enginesSection.economizerDateOfLastCleaning;
    };

    const getIsShorePowerConnectedFromPrevious = (): boolean => {
        const aolDataItems = aolGroups.flatMap((a) => a.aolDataItems);
        const previousDate = subtractDateDays(selectedAolDataItem.reportDate, 1);
        const previousAol = aolDataItems.find((a) => isDateSame(a.reportDate, previousDate, 'day'));

        return (
            !!selectedAolDataItem?.aolItem?.dryDockingTime &&
            selectedAolDataItem?.aolItem?.dryDockingTime.valueOf() > 0 &&
            !!previousAol?.noonReport?.enginesSection?.isShorePowerConnected
        );
    };

    const onVmChange = () => {
        setEnginesDataViewModel({ ...enginesDataViewModel });
    };

    const addConsumptionRow = (consumption: Consumption[]) => {
        consumption.forEach((c) => {
            enginesDataViewModel.consumption.push(c);
        });

        if (consumption.length > 0 && consumption[0].engineDataType === EngineDataTypeEnum.Boilers) {
            const usageTypes = Object.keys(BoilersUsageTypeEnum)
                .filter((v) => !isNaN(Number(v)))
                .map(
                    (type) =>
                        ({
                            BoilerConsumptionId: 0,
                            NoonReportId: selectedAolDataItem.noonReport?.metadata?.noonReportId ?? 0,
                            BoilersUsageType: Number.parseInt(type) as BoilersUsageTypeEnum,
                            FuelTypeId: consumption[0].fuelType,
                            QuantityMt: 0,
                        } as BoilerConsumptionV5)
                );
            enginesDataViewModel.boilerConsumptionV5 = [...enginesDataViewModel.boilerConsumptionV5, ...usageTypes];
        }

        onVmChange();
    };

    const getMaxHoursInReport = () => {
        return roundTo2Decimals(selectedAolDataItem?.reportHours ?? 0);
    };

    const save = () => {
        const data = new NoonReportEnginesSectionFactory().createBM(enginesDataViewModel);
        if (!data.sectionData || data.sectionData.section === 0)
            data.sectionData = {
                noonReportId: 0,
                section: NoonReportSectionEnum.Engines,
                isApproved: false,
                isCompleted: false,
                role: 1,
            };

        if (!enginesDataViewModel.hasInvalidFields) {
            saveSection(data);
        }
    };

    if (!selectedAolDataItem) return <></>;
    return (
        <>
            {isLoadingEndpoint('GetVoyageFuelPrices') && (
                <SpinnerLoaderComponent message={isLoadingEndpoint('GetVoyageFuelPrices').message} clearDefaultText={true} isFeatureSpinner={true} />
            )}
            <EnginesSectionWrapper>
                {numberOfColumns === 1 && (
                    <>
                        <SectionInnerWrapper>
                            <MainEngineSectionComponent
                                enginesDataVM={enginesDataViewModel}
                                isReadOnly={isReadOnly}
                                onChange={onVmChange}
                                hoursInReport={getMaxHoursInReport()}
                                vessel={vessel}
                                reportType={selectedAolDataItem.reportType}
                                aolDataItem={selectedAolDataItem}
                                hasEmptyValues={hasEmptyMainValues}
                                addConsumptionRow={addConsumptionRow}
                            ></MainEngineSectionComponent>
                            <AuxiliaryEnginesSectionComponent
                                enginesDataVM={enginesDataViewModel}
                                isReadOnly={isReadOnly}
                                onChange={onVmChange}
                                hasEmptyValues={hasEmptyAuxValues}
                                vessel={vessel}
                                aolVersion={selectedAolDataItem.aolVersion}
                                aolDataItem={selectedAolDataItem}
                                addConsumptionRow={addConsumptionRow}
                            ></AuxiliaryEnginesSectionComponent>
                            {selectedAolDataItem.aolDataItemProperties.isDryDockingAolReport && (
                                <ShorePowerSectionComponent
                                    enginesDataVM={enginesDataViewModel}
                                    isReadOnly={isReadOnly}
                                    onChange={onVmChange}
                                    aolDataItem={selectedAolDataItem}
                                    hoursInReport={getMaxHoursInReport()}
                                    hasEmptyValues={hasEmptyIncineratorValues}
                                />
                            )}
                            <BoilersSectionComponent
                                enginesDataVM={enginesDataViewModel}
                                isReadOnly={isReadOnly}
                                onChange={onVmChange}
                                aolDataItem={selectedAolDataItem}
                                hasEmptyValues={hasEmptyBoilerValues}
                                vessel={vessel}
                                addConsumptionRow={addConsumptionRow}
                            ></BoilersSectionComponent>
                            <PumpsAndFansSectionComponent
                                enginesDataVM={enginesDataViewModel}
                                isReadOnly={isReadOnly}
                                onChange={onVmChange}
                                aolDataItem={selectedAolDataItem}
                                hasEmptyValues={hasEmptyPumpAndFansValues}
                                vessel={vessel}
                            ></PumpsAndFansSectionComponent>
                            <IncineratorSectionComponent
                                enginesDataVM={enginesDataViewModel}
                                isReadOnly={isReadOnly}
                                onChange={onVmChange}
                                aolDataItem={selectedAolDataItem}
                                hasEmptyValues={hasEmptyIncineratorValues}
                                addConsumptionRow={addConsumptionRow}
                            ></IncineratorSectionComponent>
                            {hasEconomizer && (
                                <EconomizerSectionComponent
                                    enginesDataVM={enginesDataViewModel}
                                    isReadOnly={isReadOnly}
                                    onChange={onVmChange}
                                    aolDataItem={selectedAolDataItem}
                                    hasEmptyValues={hasEmptyEconomizerValues}
                                ></EconomizerSectionComponent>
                            )}
                        </SectionInnerWrapper>
                    </>
                )}
                {numberOfColumns !== 1 && (
                    <>
                        <SectionInnerWrapper>
                            <ColumnsWrapper>
                                <Column>
                                    <MainEngineSectionComponent
                                        enginesDataVM={enginesDataViewModel}
                                        isReadOnly={isReadOnly}
                                        onChange={onVmChange}
                                        hoursInReport={getMaxHoursInReport()}
                                        vessel={vessel}
                                        reportType={selectedAolDataItem.reportType}
                                        aolDataItem={selectedAolDataItem}
                                        hasEmptyValues={hasEmptyMainValues}
                                        addConsumptionRow={addConsumptionRow}
                                    ></MainEngineSectionComponent>
                                    <AuxiliaryEnginesSectionComponent
                                        enginesDataVM={enginesDataViewModel}
                                        isReadOnly={isReadOnly}
                                        onChange={onVmChange}
                                        hasEmptyValues={hasEmptyAuxValues}
                                        vessel={vessel}
                                        aolVersion={selectedAolDataItem.aolVersion}
                                        aolDataItem={selectedAolDataItem}
                                        addConsumptionRow={addConsumptionRow}
                                    ></AuxiliaryEnginesSectionComponent>
                                    {selectedAolDataItem.aolDataItemProperties.isDryDockingAolReport && (
                                        <ShorePowerSectionComponent
                                            enginesDataVM={enginesDataViewModel}
                                            isReadOnly={isReadOnly}
                                            onChange={onVmChange}
                                            aolDataItem={selectedAolDataItem}
                                            hoursInReport={getMaxHoursInReport()}
                                            hasEmptyValues={hasEmptyIncineratorValues}
                                        />
                                    )}
                                </Column>
                                <Column>
                                    <BoilersSectionComponent
                                        enginesDataVM={enginesDataViewModel}
                                        isReadOnly={isReadOnly}
                                        onChange={onVmChange}
                                        aolDataItem={selectedAolDataItem}
                                        hasEmptyValues={hasEmptyBoilerValues}
                                        vessel={vessel}
                                        addConsumptionRow={addConsumptionRow}
                                    ></BoilersSectionComponent>
                                    <PumpsAndFansSectionComponent
                                        enginesDataVM={enginesDataViewModel}
                                        isReadOnly={isReadOnly}
                                        onChange={onVmChange}
                                        aolDataItem={selectedAolDataItem}
                                        hasEmptyValues={hasEmptyPumpAndFansValues}
                                        vessel={vessel}
                                    ></PumpsAndFansSectionComponent>
                                    <IncineratorSectionComponent
                                        enginesDataVM={enginesDataViewModel}
                                        isReadOnly={isReadOnly}
                                        onChange={onVmChange}
                                        aolDataItem={selectedAolDataItem}
                                        hasEmptyValues={hasEmptyIncineratorValues}
                                        addConsumptionRow={addConsumptionRow}
                                    ></IncineratorSectionComponent>
                                    {hasEconomizer && (
                                        <EconomizerSectionComponent
                                            enginesDataVM={enginesDataViewModel}
                                            isReadOnly={isReadOnly}
                                            onChange={onVmChange}
                                            aolDataItem={selectedAolDataItem}
                                            hasEmptyValues={hasEmptyEconomizerValues}
                                        ></EconomizerSectionComponent>
                                    )}
                                </Column>
                            </ColumnsWrapper>
                        </SectionInnerWrapper>
                    </>
                )}
            </EnginesSectionWrapper>
        </>
    );
};

export default EnginesSectionComponent;
